import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, BehaviorSubject, from, throwError, EMPTY, of } from 'rxjs';
import { catchError, switchMap, tap, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  Auth,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  OAuthProvider,
  UserCredential
} from '@angular/fire/auth';
import {
  AccountSummary,
  AuthResponse,
  LoginRequest,
  TeamSummary
} from '@app/models/auth.model';
import { NGXLogger } from 'ngx-logger';
import { injectQueryClient } from '@ngneat/query';
import { productCode } from '@environment/environment.commons';
import { ARIA_TOAST_SERVICE } from 'app/tokens/toast.tokens';
import { clearLocalStorageKeepingPersistent } from '@app/utils/util/persistent-storage';
import { clearReturnUrl, consumeReturnUrl, DEFAULT_AUTHENTICATED_ROUTE } from '@app/utils/util/return-url';


type AuthState = {
  account: AccountSummary | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private http = inject(HttpClient);
  private auth = environment.mockEnabled ? null as any : inject(Auth);
  private router = inject(Router);
  private logger = inject(NGXLogger);
  private baseUrl = `${environment.API_AUTH_LOGIN}/api/v1/auth`;
  private toastService = inject(ARIA_TOAST_SERVICE);
  private queryClient = injectQueryClient();
  
  // State con RxJS
  private state$ = new BehaviorSubject<AuthState>({
    account: null,
    token: null,
    isAuthenticated: false,
    loading: false,
    error: null
  });


  // Sources
  login$ = new Subject<LoginRequest>();
  loginWithMicrosoft$ = new Subject<void>();
  logout$ = new Subject<void>();
  forgotPassword$ = new Subject<{ email: string }>();
  checkAuthState$ = new Subject<void>();

  private destroy$ = new Subject<void>();

  constructor() {
    // Inicializar estado desde localStorage
    this.checkAuthState();

    // Configurar reducers
    this.login$.pipe(
      tap(() => {
        this.updateState({ loading: true, error: null });
      }),
      switchMap(request =>
        this.loginApi(request).pipe(
          tap(response => this.handleAuthSuccess(response)),
          catchError(error => {
            this.handleAuthError(error);
            return EMPTY;
          })
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe();

    this.loginWithMicrosoft$.pipe(
      tap(() => {
        this.updateState({ loading: true, error: null });
      }),
      switchMap(() =>
        this.loginWithMicrosoftApi().pipe(
          tap(response => this.handleAuthSuccess(response)),
          catchError(error => {
            this.handleAuthError(error);
            return EMPTY;
          })
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe();

    this.logout$.pipe(
      tap(() => {
        this.updateState({ loading: true });
      }),
      switchMap(() => this.logoutApi()),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.clearAuthData();
        clearReturnUrl();
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        this.clearAuthData();
        clearReturnUrl();
        this.router.navigate(['/auth/login']);
      }
    });

    this.forgotPassword$.pipe(
      tap(() => {
        this.updateState({ loading: true, error: null });
      }),
      switchMap(request => this.forgotPasswordApi(request.email)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.updateState({ loading: false });
      },
      error: (error) => {
        let errorMessage = 'Error al enviar el correo de recuperación';
        if (error.code === 'auth/user-not-found') {
          errorMessage = 'No existe una cuenta con este correo electrónico';
        } else if (error.code === 'auth/invalid-email') {
          errorMessage = 'El correo electrónico no es válido';
        }

        this.updateState({
          loading: false,
          error: errorMessage
        });
      }
    });

    this.checkAuthState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.checkAuthState();
    });
  }

  /**
   * Helper para actualizar el estado
   */
  private updateState(partial: Partial<AuthState>): void {
    this.state$.next({
      ...this.state$.value,
      ...partial
    });
  }

  /**
   * Check authentication state based on localStorage
   */
  checkAuthState(): void {
    const userJson = localStorage.getItem('auth_user');

    let account = null;
    if (userJson) {
      try {
        account = JSON.parse(userJson);
      } catch (e) {
        this.logger.error('Error parsing user data', e);
      }
    }

    if (account) {
      this.updateState({
        isAuthenticated: true,
        account
      });

      if (this.router.url.startsWith('/auth')) {
        this.router.navigateByUrl(consumeReturnUrl() ?? DEFAULT_AUTHENTICATED_ROUTE);
      }
    } else {
      this.clearAuthData();
    }
  }

  /**
   * Login with email and password using Firebase Authentication
   */
  private loginApi(request: LoginRequest): Observable<AuthResponse> {
    this.logger.info('Attempting login with email:', request.email);
    if (environment.mockEnabled) {
      return this.http.post<AuthResponse>(`${this.baseUrl}/session/login`, {
        ...request,
        firebaseToken: 'mock-firebase-token-xyz',
        productCode: productCode
      });
    }
    return from(
      signInWithEmailAndPassword(this.auth, request.email, request.password)
    ).pipe(
      catchError(error => {
        this.logger.error('Firebase login error:', error);
        return throwError(() => error);
      }),
      switchMap((userCredential: UserCredential) =>
        from(userCredential.user.getIdToken()).pipe(
          catchError(error => {
            this.logger.error('Error getting token:', error);
            return throwError(() => error);
          }),
          switchMap((firebaseToken: string) =>
            this.http.post<AuthResponse>(`${this.baseUrl}/session/login`, {
              ...request,
              firebaseToken,
              productCode: productCode
            }).pipe(
              tap(resp => {
                if (!resp.firebaseToken) {
                  resp.firebaseToken = firebaseToken;
                }
              }),
              catchError(error => {
                this.logger.error('API login error:', error);
                return throwError(() => error);
              })
            )
          )
        )
      )
    );
  }

  /**
   * Login with Microsoft account using Firebase Authentication
   */
  private loginWithMicrosoftApi(): Observable<AuthResponse> {
    this.logger.info('Attempting login with Microsoft');
    if (environment.mockEnabled) {
      return this.http.post<AuthResponse>(`${this.baseUrl}/session/login`, {
        firebaseToken: 'mock-firebase-token-xyz',
        productCode: productCode
      });
    }
    const provider = new OAuthProvider('microsoft.com');

    if (environment.firebaseProvider) {
      const { prompt, tenant } = environment.firebaseProvider;

      provider.setCustomParameters({
        prompt: prompt,
        tenant: tenant
      });
    }

    return from(
      signInWithPopup(this.auth, provider)
    ).pipe(
      catchError(error => {
        this.logger.error('Firebase Microsoft login error:', error);
        return throwError(() => error);
      }),
      switchMap((userCredential: UserCredential) =>
        from(userCredential.user.getIdToken()).pipe(
          catchError(error => {
            this.logger.error('Error getting token:', error);
            return throwError(() => error);
          }),
          switchMap((firebaseToken: string) =>
            this.http.post<AuthResponse>(`${this.baseUrl}/session/login`, {
              firebaseToken,
              productCode: productCode
            }).pipe(
              tap(resp => {
                if (!resp.firebaseToken) {
                  resp.firebaseToken = firebaseToken;
                }
              }),
              catchError(error => {
                this.logger.error('API login error:', error);
                return throwError(() => error);
              })
            )
          )
        )
      )
    );
  }

  /**
   * Send password reset email using Firebase Authentication
   */
  private forgotPasswordApi(email: string): Observable<void> {
    this.logger.info('Sending password reset email to:', email);
    if (environment.mockEnabled) {
      return of(undefined);
    }
    return from(sendPasswordResetEmail(this.auth, email)).pipe(
      catchError(error => {
        this.logger.error('Error sending reset email:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout user
   */
  private logoutApi(): Observable<void> {
    if (environment.mockEnabled) {
      return this.http.post<void>(`${this.baseUrl}/session/logout`, {});
    }
    return this.http.post(`${this.baseUrl}/session/logout`, {}).pipe(
      switchMap(() => from(this.auth.signOut()) as Observable<void>),
      catchError(() => {
        return from(this.auth.signOut()) as Observable<void>;
      })
    );
  }

  /**
   * Handle successful authentication
   */
  private handleAuthSuccess(response: AuthResponse): void {
    if (!response.account.permissions || response.account.permissions.length === 0) {
      this.updateState({
        loading: false,
        error: 'No estás asignado/a a ningún equipo'
      });

      this.toastService.showError(
        'No estás asignado/a a ningún equipo',
        'Ponte en contacto con el administrador de tu empresa para que te de los permisos necesarios para usar ARIA.',
        'bottom'
      );

      return;
    }

    localStorage.setItem('auth_user', JSON.stringify(response.account));
    // Guardar el token para que el interceptor lo use como header Authorization
    if (response.firebaseToken) {
      localStorage.setItem('auth_token', response.firebaseToken);
    }

    this.updateState({
      isAuthenticated: true,
      token: response.firebaseToken || 'cookie-based',
      account: response.account,
      loading: false,
      error: null
    });

    this.router.navigateByUrl(consumeReturnUrl() ?? DEFAULT_AUTHENTICATED_ROUTE);
  }

  /**
   * Handle authentication error
   */
  private handleAuthError(error: any): void {
    this.logger.error('Auth error:', error);

    let title = 'Ocurrió un error';
    let errorMessage = 'Error de autenticación';
    if (error?.error?.errorCode === 'PRODUCT_ACCESS_DENIED') {
      title = 'No estás asignado/a a ningún equipo';
      errorMessage = 'Ponte en contacto con el administrador de tu empresa para que te de los permisos necesarios para usar ARIA.';
    } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      errorMessage = 'Credenciales inválidas';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Demasiados intentos fallidos. Intente más tarde';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Error de conexión. Por favor verifique su internet';
    } else if (error.code === 'auth/popup-closed-by-user') {
      errorMessage = 'Se canceló el inicio de sesión';
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      errorMessage = 'La cuenta ya existe con otro proveedor de autenticación';
    } else if (error.message) {
      errorMessage += ' - ' + error.message;
    }

    this.updateState({
      loading: false,
      error: errorMessage
    });

    this.toastService.showError(
      title,
      errorMessage,
      'bottom'
    );
  }

  /**
   * Limpiar datos de autenticación
   */
  clearAuthData(): void {
    clearLocalStorageKeepingPersistent();
    // Sin esto, el siguiente usuario de la pestaña vería las queries del anterior
    this.queryClient.clear();

    this.updateState({
      isAuthenticated: false,
      account: null,
      token: null,
      loading: false,
      error: null
    });

    this.logger.info('Auth data cleared successfully');
  }

  /**
   * Check if user has specific permission
   */
  hasPermissionFor(permissionId: string): boolean {
    const user = this.state$.value.account;
    if (user && user.permissions) {
      return user.permissions.some((p: string) => p === permissionId);
    }
    return false;
  }

  hasPermissionsFor(permissionIds: string[]): boolean {
    const user = this.state$.value.account;
    if (user && user.permissions) {
      return permissionIds.every(permissionId => {
        return user.permissions?.some((p: string) => p === permissionId);
      });
    }
    return false;
  }

  canAccessFeature(modulePreffix: string): boolean {
    const user = this.state$.value.account;
    if (user && user.permissions) {
      return user.permissions.some((p: string) => p.startsWith(modulePreffix));
    }
    return false;
  }

  get isAuthenticated(): boolean {
  return this.state$.value.isAuthenticated;
}

  get account(): AccountSummary | null {
    return this.state$.value.account;
  }
  
  get token(): string | null {
    return this.state$.value.token;
  }

  get isLoading(): boolean {
    return this.state$.value.loading;
  }

  get error(): string | null {
    return this.state$.value.error;
  }

  get teams(): TeamSummary[] | null {
    return this.state$.value.account?.teams || null;
  }
}
